import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Themed, ThemeName } from "@components/atoms/Themed"
import { cn } from "@utils/tw"
import { Link } from "eddev/routing"
import { useEffect, useState } from "react"
import Marquee from "react-fast-marquee"
import { useWindowSize } from "usehooks-ts"
import { ResponsiveImageFragment } from "../../types.graphql"
import { ScrollReveal } from "@components/motion/Transitioned"
import { FlowgramImage, FlowgramImageType } from "./FlowgramImage"

export const CaseStudyCarousel = ({
  title,
  subtitle,
  flowgram,
  uri,
  images,
  theme,
  className,
}: {
  title: string
  subtitle?: string
  flowgram?: FlowgramImageType
  uri: string
  images: (ResponsiveImageFragment | null)[]
  theme?: ThemeName
  className?: string
}) => {
  const { width } = useWindowSize()
  const th = theme && theme !== "none" ? theme : "black"
  const isMobile = width < 900
  const [playing, setPlaying] = useState(false)
  const speed = isMobile ? 15 : 50

  useEffect(() => {
    if (isMobile) {
      setPlaying(true)
    } else {
      setPlaying(false)
    }
  }, [isMobile])

  return (
    <ScrollReveal className="w-full">
      <Link
        href={uri}
        className={cn("group/link w-full flex md:pr-0", className)}
        data-scroll-transition
        onMouseOver={() => setPlaying(true)}
        onMouseLeave={() => setPlaying(false)}
      >
        <Themed theme={th} className={cn("w-full")}>
          <div className="flex flex-row md:flex-nowrap items-center md:h-nextTileH type-title-s md:type-title-m isolate">
            <div className="relative h-nextTileH w-[10em] md:w-auto md:h-full md:aspect-[1/0.74] themed-fg shrink-0 flex flex-col justify-between">
              {/* Background color */}
              <div
                data-transition="scale"
                className="absolute inset-0 themed-bg rounded-lg -z-10 transition-radius group-hover/link:rounded-xl transition-nice"
              ></div>
              {/* Text */}
              <div className="relative flex flex-col p-7 gap-4 group-hover/link:scale-[1.01] transition-transform transition-nice">
                <div data-transition="fade-up" className="type-label-s-caps">
                  Case Study
                </div>
                <div data-transition="fade-up" className="text-balance">
                  {title}
                </div>
              </div>

              {/* details */}
              <div className="flex gap-3 items-end px-7 pb-7 w-full md:w-[80%] self-end transition-nice">
                <p className="type-label-m text-balance" data-transition="fade-up">
                  {subtitle}
                </p>
                {flowgram && (
                  <FlowgramImage flowgramImage={flowgram} className="w-[3em] md:w-[3.3em] shrink-0" strokeHeavy />
                )}
              </div>

              <div className="hidden md:flex flex-col justify-center md:col-span-1 transition-opacity duration-500 opacity-0 group-hover/link:opacity-100 bottom-7 left-7 absolute">
                <svg className="w-7 h-7" viewBox="0 0 37 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="0.5" width="35" height="34" rx="17" stroke="currentColor" />
                  <path
                    d="M17.662 21.94L20.91 18.678H13.686V17.53H20.91L17.662 14.268H19.342L23.066 18.104L19.342 21.94H17.662Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div>
            <div
              className="flex w-full h-nextTileH md:h-full overflow-hidden max-md:rounded-l-xl max-md:-mr-grid-margin md:rounded-xl"
              data-transition="fade"
            >
              {env.client && (
                <Marquee speed={speed} autoFill play={playing} className="flex items-center">
                  {images.map((image, index) => (
                    <ResponsiveImage
                      key={index}
                      {...image}
                      className="h-nextTileH w-auto flex lex rounded-xl overflow-hidden"
                    />
                  ))}
                </Marquee>
              )}
            </div>
          </div>
        </Themed>
      </Link>
    </ScrollReveal>
  )
}
